import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { GHS } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export function CartDrawer({ open, onOpenChange }) {
  const { items, updateQty, removeItem, total } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => { onOpenChange(false); navigate("/checkout"); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-brand-bg w-full sm:max-w-md border-brand/10 flex flex-col p-0">
        <SheetHeader className="p-6 border-b border-brand/10">
          <SheetTitle className="font-display text-2xl text-brand text-left">Your Cart</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-brand-muted gap-4 p-6">
            <ShoppingBag className="w-12 h-12 opacity-40" />
            <p>Your cart is empty.</p>
            <Button data-testid="cart-browse-btn" onClick={() => { onOpenChange(false); navigate("/shop"); }}
              className="bg-brand hover:bg-brand-hover text-white rounded-full">Browse Products</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="cart-items">
              {items.map((it) => (
                <div key={it.product_id} className="flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                  <img src={it.image} alt={it.name} className="w-20 h-20 object-cover rounded-sm bg-brand-surface" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-brand leading-tight">{it.name}</p>
                    <p className="text-xs text-brand-muted mt-0.5">{GHS(it.price)} · {it.unit}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-brand/20 rounded-full">
                        <button data-testid={`cart-dec-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-1.5 text-brand"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-6 text-center text-sm">{it.quantity}</span>
                        <button data-testid={`cart-inc-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-1.5 text-brand"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button data-testid={`cart-remove-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="text-brand-muted hover:text-brand-terracotta"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-brand">{GHS(it.price * it.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-brand/10 p-6 space-y-4">
              <div className="flex justify-between text-brand font-medium">
                <span>Subtotal</span>
                <span data-testid="cart-total">{GHS(total)}</span>
              </div>
              <p className="text-xs text-brand-muted">Pay with cash on delivery. Delivery arranged after you place your order.</p>
              <Button data-testid="checkout-btn" onClick={goCheckout} className="w-full bg-brand hover:bg-brand-hover text-white rounded-full h-12">
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
